import { unitStrict } from '@apparel/core/styles/unit';
import { call } from '@apparel/core/utils/functions';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DecrementButton } from './DecrementButton';
import { IncrementButton } from './IncrementButton';
import { NumericFieldProps } from './NumericFieldProps';
import { decrement, increment, mapValue } from './utils';

const useStyles = makeStyles(
    () => {
        const sizeFunc = ({ size = 36 }: NumericFieldProps) => unitStrict(size!);
        const widthFunc = ({ width = 140, fullWidth }: NumericFieldProps) => (fullWidth ? '' : width);

        return {
            root: {
                width: widthFunc,
                padding: 0,
                backgroundColor: '#fff',

                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999 !important',
                },
            },

            fieldset: {
                border: '1px solid #ccc',
                borderRadius: 2,
            },

            focused: {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#666 !important',
                    borderWidth: '1px !important',
                },
            },

            button: {
                width: sizeFunc,
                height: sizeFunc,
                borderRadius: 0,
            },

            input: {
                padding: 0,
                textAlign: 'center',
                height: sizeFunc,
                lineHeight: sizeFunc,
            },
        };
    },
    {
        name: 'NumericField',
    },
);

export function NumericField(props: NumericFieldProps) {
    const { value, size, step = 1, min, max, onChange, ...rest } = props;
    const options = useMemo(() => ({ step, min, max }), [step, min, max]);
    const [currentValue, setCurrentValue] = useState<number | string>(mapValue(value, options));
    const classes = useStyles(props);

    const setValue = useCallback(
        (value: any, commit = true) => {
            let value$ = value;
            if (commit) {
                value$ = mapValue(value$, options);
            }

            setCurrentValue(value$);
            call(onChange, mapValue(value$, options));
        },
        [options, onChange, setCurrentValue],
    );

    const handleChange = useCallback(
        (event) => {
            event.preventDefault();
            setValue(event.target.value, false);
        },
        [setValue],
    );

    const handleBlur = useCallback(
        (event) => {
            event.preventDefault();
            setValue(event.target.value);
        },
        [setValue],
    );

    const handleDecrement = useCallback(() => {
        setValue(decrement(currentValue, options));
    }, [options, currentValue, setCurrentValue]);

    const handleIncrement = useCallback(() => {
        setValue(increment(currentValue, options));
    }, [options, currentValue, setValue]);

    useEffect(() => {
        if (value !== currentValue) {
            setCurrentValue(mapValue(value, options));
        }
    }, [setCurrentValue, value]);

    return (
        <OutlinedInput
            {...rest}
            classes={{
                root: classes.root,
                input: classes.input,
                notchedOutline: classes.fieldset,
                focused: classes.focused,
            }}
            value={currentValue}
            onChange={handleChange}
            onBlur={handleBlur}
            startAdornment={<DecrementButton className={classes.button} onClick={handleDecrement} />}
            endAdornment={<IncrementButton className={classes.button} onClick={handleIncrement} />}
        />
    );
}

export default NumericField;
